import React from "react";
import Card from "react-bootstrap/Card";
import { ImPointRight } from "react-icons/im";

function calculateAge(birthDate) {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

function calculateExperience(startDate) {
  const today = new Date();
  const start = new Date(startDate);
  let years = today.getFullYear() - start.getFullYear();
  const monthDiff = today.getMonth() - start.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < start.getDate())) {
    years--;
  }
  return years;
}

function AboutCard() {
  const age = calculateAge("1997-09-01");
  const experience = calculateExperience("2021-03-01");

  return (
    <Card className="quote-card-view">
      <Card.Body>
      
      <blockquote className="blockquote mb-0">
  <h2 className="section-title"><span className="purple">About Me</span></h2>

  <div className="about-introduction">
    <p style={{ textAlign: "justify" }}>
      Hi, I'm <span className="purple">Raed Madouri</span>, a software engineer living in <span className="purple">Tunis, Tunisia</span>. I've been in this field for a few years now, and what keeps me going is the challenge of solving real-world problems with clean, effective code. It's not just about technology,  it's about building tools that make life easier, more efficient, or simply more enjoyable.
    </p>
  </div>

  <h3 className="subsection-title">Professional Background</h3>
  <div className="about-professional">
    <p style={{ textAlign: "justify" }}>
      I currently work at <span className="highlight">Yebni Information & Communication</span> as a senior software engineer. Most of the time, I'm working on full-stack applications using <span className="tech-highlight">Java, Spring Boot, Flutter, and PostgreSQL</span>. I also have a deep interest in geospatial technology and regularly use <span className="tech-highlight">PostGIS and OpenLayers</span> for mapping and spatial analytics, especially in agriculture and infrastructure-related projects.
    </p>

    <p style={{ textAlign: "justify" }}>
      My strength lies in building solid REST APIs, designing microservices, and deploying apps on cloud platforms like AWS and GCP. I’m comfortable writing backend logic, optimizing databases, and making sure things scale without breaking. On the frontend side, I value simplicity, responsiveness, and good UX,  no one likes clunky interfaces.
    </p>

    <p style={{ textAlign: "justify" }}>
      I'm hands-on from planning to deployment, and I enjoy collaborating across teams to make sure everyone’s aligned. My approach is simple: understand the need first, then write code that solves it without overengineering.
    </p>
  </div>

  <h3 className="subsection-title">Technical Expertise</h3>
  <div className="about-expertise">
    <div className="expertise-container">
      <div className="expertise-column">
        <h4>Backend Development</h4>
        <ul className="about-skills">
          <li className="about-activity"><ImPointRight /> Java & Spring Boot ecosystem (Spring Data, Spring Security, Spring Cloud)</li>
          <li className="about-activity"><ImPointRight /> RESTful API design and implementation</li>
          <li className="about-activity"><ImPointRight /> Microservices architecture</li>
          <li className="about-activity"><ImPointRight /> Database optimization (PostgreSQL, MySQL)</li>
        </ul>
      </div>

      <div className="expertise-column">
        <h4>Frontend & Mobile</h4>
        <ul className="about-skills">
          <li className="about-activity"><ImPointRight /> Flutter for cross-platform mobile development</li>
          <li className="about-activity"><ImPointRight /> Responsive web design with modern JavaScript frameworks</li>
          <li className="about-activity"><ImPointRight /> UI/UX implementation with focus on accessibility</li>
          <li className="about-activity"><ImPointRight /> State management and performance optimization</li>
        </ul>
      </div>

      <div className="expertise-column">
        <h4>Geospatial Development</h4>
        <ul className="about-skills">
          <li className="about-activity"><ImPointRight /> PostGIS for spatial data management</li>
          <li className="about-activity"><ImPointRight /> OpenLayers for interactive mapping applications</li>
          <li className="about-activity"><ImPointRight /> Geospatial analysis and visualization</li>
          <li className="about-activity"><ImPointRight /> Location-based service implementation</li>
        </ul>
      </div>

      <div className="expertise-column">
        <h4>DevOps & Architecture</h4>
        <ul className="about-skills">
          <li className="about-activity"><ImPointRight /> Docker containerization & Kubernetes orchestration</li>
          <li className="about-activity"><ImPointRight /> CI/CD pipeline implementation</li>
          <li className="about-activity"><ImPointRight /> Cloud infrastructure (AWS, GCP)</li>
          <li className="about-activity"><ImPointRight /> Software architecture patterns & best practices</li>
        </ul>
      </div>
    </div>
  </div>

  <h3 className="subsection-title">Beyond Code</h3>
  <div className="about-personal">
    <p style={{ textAlign: "justify" }}>
      Outside of coding, I have a few strong passions, one of them is <strong>cars</strong>, especially <strong>BMWs</strong>. I own an <span className="purple">E90</span> and I’m deep into the world of drifting, tuning, and learning how every piece of the car works. I enjoy tweaking performance, experimenting with setups, and occasionally spending hours reading forums and testing tools like NCS Expert or INPA. It’s not just a hobby,  it’s another way I understand systems.
    </p>

    <p style={{ textAlign: "justify" }}>
      I'm also into strategy games, games that make you think several steps ahead. They’ve taught me a lot about planning and system design, which actually translates pretty well into software engineering. I like to write as well, especially tutorials and personal notes that break down things I’ve figured out the hard way.
    </p>

    <p style={{ textAlign: "justify" }}>
      Traveling is another part of my life that keeps me inspired. New places, new people, new challenges, all of it feeds into the way I approach work and creativity. I’ve also mentored a few junior developers and participated in community events, because I believe knowledge should always be shared.
    </p>
  </div>

  <h3 className="subsection-title">This Portfolio</h3>
  <div className="about-portfolio">
    <p style={{ textAlign: "justify" }}>
    This portfolio is more than just a website, it's my personal notebook. A space where I share what I’ve built, fixed, learned, and lived.
    You won’t find abstract theory or recycled tutorials here, only things I’ve truly experienced.
    </p>

    <p style={{ textAlign: "justify" }}>
    Every post comes from real work, long nights, failed attempts, small wins, and honest effort.
    Whether it’s Spring Boot, spatial data, server setups, car diagnostics, mining, or trading, 
    </p>

    <p style={{ textAlign: "justify" }}>
    I write about what I’ve explored with my own hands. Sometimes I also share moments from gaming, traveling, or ideas that shaped me far from the screen.
    </p>

    <p style={{ textAlign: "justify" }}>
    This is how I keep track of my journey, one real story at a time, hoping someone finds something useful in it too.
    </p>
  </div>

  <h3 className="subsection-title">Let's Connect</h3>
  <div className="about-connect">
    <p style={{ textAlign: "justify" }}>
      If you're working on something interesting, want to talk tech, collaborate on a project, or even chat about BMW setups,  don’t hesitate to reach out. I’m always up for good conversations and new opportunities.
    </p>
  </div>

  <div className="about-philosophy">
    <p style={{ color: "rgb(155 126 172)", fontSize: "1.2em", fontStyle: "italic", margin: "2rem 0" }}>
      "Build what matters. Good code solves problems. Great code understands people."
    </p>
    <footer className="blockquote-footer">Raed Madouri</footer>
  </div>

  <div className="key-skills-container">
    <h3 className="subsection-title">Core Skills & Technologies</h3>
    <div className="key-skills">
      <span className="skill-tag">Java,  </span>
      <span className="skill-tag">Spring Boot,  </span>
      <span className="skill-tag">Flutter,  </span>
      <span className="skill-tag">PostgreSQL,  </span>
      <span className="skill-tag">PostGIS,  </span>
      <span className="skill-tag">OpenLayers,  </span>
      <span className="skill-tag">RESTful APIs,  </span>
      <span className="skill-tag">Microservices,  </span>
      <span className="skill-tag">Cloud Architecture,  </span>
      <span className="skill-tag">Docker,  </span>
      <span className="skill-tag">CI/CD,  </span>
      <span className="skill-tag">INPA & NCS_Expert,  </span>
      <span className="skill-tag">Technical Writing </span>
    </div>
  </div>
</blockquote>


      </Card.Body>
    </Card>
  );
}

export default AboutCard;
